import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import {
	Button,
	Center,
	HStack,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	useDisclosure,
	Text,
	Box,
	useToast,
	Flex,
	Grid,
	FormControl,
	FormLabel,
	Skeleton,
	Select,
	FormErrorMessage
} from '@chakra-ui/react';

import { useGetRegionals } from 'hooks/regionals';
import { useGetUnits } from 'hooks/units';

import {
	useImportBeneficiaries,
	usePaginateBeneficiaries
} from 'hooks/beneficiaries';

import modeloImportacao from 'assets/csv/modelo_importacao_vidas.csv';

const schema = yup.object().shape({
	unit_id: yup.string().required('O campo Unidade é obrigatório'),
	campaign_id: yup.string().required('Escolha uma Campanha')
});

const ImportBeneficiaries = () => {
	const { data: dataR, loading: loadingRegionals } = useGetRegionals();
	const { data: dataU, loading: loadingUnits } = useGetUnits();

	const { isOpen, onOpen, onClose } = useDisclosure();
	const [paginateBeneficiaries] = usePaginateBeneficiaries();

	const regionals = dataR?.listRegionals || [];
	const units = dataU?.listUnits || [];
	const toast = useToast();

	const methods = useForm({
		mode: 'onBlur',
		reValidateMode: 'onBlur',
		resolver: yupResolver(schema)
	});

	const {
		register,
		formState: { errors },
		getValues,
		setError,
		reset,
		watch
	} = methods;

	const wRegional = watch('regional_id', null);
	const wUnit = watch('unit_id', null);

	const { getRootProps, getInputProps, acceptedFiles, isDragActive } =
		useDropzone({
			accept: ['text/csv'],
			multiple: false
		});

	const handleDownload = () => {
		window.open(modeloImportacao, '_blank');
	};

	const [importBeneficiaries, { loading }] = useImportBeneficiaries({
		onSuccess: () => {
			paginateBeneficiaries();
			toast({
				title: 'Importação realizada com sucesso.',
				status: 'success',
				duration: 5000,
				isClosable: true
			});
			onClose();
			reset();
		},
		onError: () => {
			toast({
				title: 'Ocorreu um erro',
				status: 'error',
				duration: 5000,
				isClosable: true
			});
		}
	});

	const onSubmit = () => {
		const file = acceptedFiles[0];

		if (!file) {
			setError('file', {
				type: 'custom',
				message: 'Anexe um arquivo para realizar a importação'
			});
			return;
		}

		const unitId = getValues('unit_id');

		importBeneficiaries({
			variables: {
				unit_id: unitId,
				file
			}
		});
	};

	return (
		<>
			<Button colorScheme="green" size="md" onClick={onOpen}>
				Importar Vidas
			</Button>
			<Modal
				isCentered
				onClose={onClose}
				isOpen={isOpen}
				motionPreset="slideInBottom"
				size="lg"
			>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>Importação de Vidas</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<FormProvider {...methods}>
							<form style={{ width: '100%' }}>
								<Flex direction="column" w="100%">
									<Grid
										templateColumns={{
											sm: '1fr',
											md: 'repeat(1, 1fr)'
										}}
										gap="24px"
									>
										<FormControl
											isInvalid={errors.regional_id}
										>
											<FormLabel
												fontWeight="bold"
												fontSize="xs"
											>
												Regional
											</FormLabel>
											<Skeleton
												height="40px"
												isLoaded={!loadingRegionals}
											>
												<Select
													placeholder="Selecione uma opção..."
													{...register('regional_id')}
												>
													{regionals.map((reg) => (
														<option value={reg.id}>
															{reg.name}
														</option>
													))}
												</Select>
											</Skeleton>
											{errors.regional_id && (
												<FormErrorMessage>
													{errors.regional_id.message}
												</FormErrorMessage>
											)}
										</FormControl>
										<FormControl isInvalid={errors.unit_id}>
											<FormLabel
												fontWeight="bold"
												fontSize="xs"
											>
												Unidade
											</FormLabel>
											<Skeleton
												height="40px"
												isLoaded={!loadingUnits}
											>
												<Select
													placeholder={
														!wRegional
															? 'Selecione uma regional'
															: 'Selecione uma opção...'
													}
													disabled={!wRegional}
													{...register('unit_id')}
												>
													{units
														?.filter(
															(unit) =>
																unit.regional
																	.id ==
																wRegional
														)
														.map((unit) => (
															<option
																value={unit.id}
															>
																{unit.name}
															</option>
														))}
												</Select>
											</Skeleton>
											{errors.unit_id && (
												<FormErrorMessage>
													{errors.unit_id.message}
												</FormErrorMessage>
											)}
										</FormControl>
										<FormControl
											isInvalid={errors.campaign_id}
										>
											<FormLabel
												fontWeight="bold"
												fontSize="xs"
											>
												Campanha
											</FormLabel>
											<Skeleton
												height="40px"
												isLoaded={!loadingUnits}
											>
												<Select
													placeholder={
														!wUnit
															? 'Selecione uma unidade'
															: 'Selecione uma opção...'
													}
													disabled={!wUnit}
													{...register('campaign_id')}
												>
													{units
														?.filter(
															(unit) =>
																unit.regional
																	.id ==
																wRegional
														)
														.map((unit) => (
															<option
																value={unit.id}
															>
																{unit.name}
															</option>
														))}
												</Select>
											</Skeleton>
											{errors.campaign_id && (
												<FormErrorMessage>
													{errors.campaign_id.message}
												</FormErrorMessage>
											)}
										</FormControl>
										<FormControl isInvalid={errors.file}>
											<Center
												align="center"
												border="1px dashed"
												borderColor="green.500"
												borderRadius="15px"
												minH="200px"
												cursor="pointer"
												{...getRootProps({
													className: 'dropzone'
												})}
											>
												<Input {...getInputProps()} />
												<Text>
													{acceptedFiles?.length ===
													0 ? (
															<Text>
																{isDragActive
																	? 'Solte os arquivos aqui'
																	: 'Arraste ou selecione uma planilha aqui'}
															</Text>
														) : (
															<Box>
																<Text mb="1em">
																Arquivo
																selecionado:{' '}
																	{
																		acceptedFiles[0]
																			.name
																	}
																</Text>
															</Box>
														)}
												</Text>
											</Center>
											{errors.file && (
												<FormErrorMessage>
													{errors.file.message}
												</FormErrorMessage>
											)}
										</FormControl>
									</Grid>
								</Flex>
							</form>
						</FormProvider>
					</ModalBody>
					<ModalFooter>
						<Button
							disabled={loading}
							mr={3}
							variant="ghost"
							size="md"
							onClick={onClose}
						>
							Cancelar
						</Button>
						<HStack spacing={4}>
							<Button
								disabled={loading}
								colorScheme="blue"
								size="md"
								onClick={handleDownload}
							>
								Baixar planilha modelo
							</Button>
							<Button
								isLoading={loading}
								disabled={loading}
								colorScheme="green"
								size="md"
								onClick={onSubmit}
							>
								Importar
							</Button>
						</HStack>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	);
};

export default ImportBeneficiaries;
